// Story 12.4 — QR scan modal for Evolution Go connection. Triggers connect
// on open; renders the QR; observes status; auto-closes when the phone
// completes the scan.
// Story 17-2 — "completó el escaneo" es `state: linked` (v0.7 reporta
// `connected` mientras muestra el QR). Si la cuenta pide passkey, no hay QR:
// se explica que el vínculo se termina en el Manager de Evolution GO.

import { Loader2 } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Modal } from '@/components/editor/dialogs/Modal';
import { Button } from '@/components/ui/button';
import { useEvolutionConnect } from '@/hooks/mutations/use-evolution-connect';
import { isQrNotReady, qrImageSrc, useEvolutionQr } from '@/hooks/queries/use-evolution-qr';
import { linkStateOf, useEvolutionStatus } from '@/hooks/queries/use-evolution-status';

interface Props {
  open: boolean;
  onClose: () => void;
  botId: number;
  botName: string;
}

export function EvolutionConnectModal({
  open,
  onClose,
  botId,
  botName,
}: Props): React.ReactElement {
  const connect = useEvolutionConnect(botId);
  const status = useEvolutionStatus(botId, { enabled: open });
  const linked = linkStateOf(status.data) === 'linked';
  const qr = useEvolutionQr(botId, {
    enabled: open && connect.data?.status === 'qr-pending' && !linked,
  });
  const triggeredRef = useRef(false);

  // Trigger connect once on open; reset on close so re-opening triggers again.
  // Code review P2 — depend on `connect.mutate` (stable reference) instead of
  // the entire `connect` object (which TanStack Query recreates on each
  // mutation state change, causing redundant effect re-evaluation).
  const connectMutate = connect.mutate;
  useEffect(() => {
    if (open && !triggeredRef.current) {
      triggeredRef.current = true;
      connectMutate(undefined, {
        onSuccess: (res) => {
          if (res.status === 'already-connected') {
            onClose();
          }
        },
      });
    }
    if (!open) {
      triggeredRef.current = false;
    }
  }, [open, connectMutate, onClose]);

  // Auto-close when the phone finished linking.
  useEffect(() => {
    if (open && linked) {
      toast.success(`WhatsApp conectado en "${botName}".`);
      onClose();
    }
  }, [open, linked, botName, onClose]);

  // Mientras Evolution GO arranca el cliente, el QR responde "todavía no"
  // (409) y la query reintenta: es espera, no error.
  const waitingQr =
    qr.isFetching && qr.data == null && (qr.error == null || isQrNotReady(qr.error));
  const isConnecting = connect.isPending || waitingQr;
  const qrError = qr.error != null && !isQrNotReady(qr.error) ? qr.error : null;
  const errorMsg = (connect.error ?? qrError) as Error | null | undefined;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Conectar WhatsApp"
      description={`Escanea este QR desde WhatsApp en tu teléfono para conectar "${botName}".`}
      footer={
        <Button variant="ghost" onClick={onClose}>
          Cerrar
        </Button>
      }
    >
      <div className="space-y-3 text-sm">
        {isConnecting && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {connect.isPending ? 'Iniciando conexión…' : 'Generando el QR…'}
          </div>
        )}

        {errorMsg && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-destructive">
            <p className="font-semibold">No se pudo obtener el QR</p>
            <p className="mt-1 text-xs">{errorMsg.message}</p>
          </div>
        )}

        {qr.data?.kind === 'passkey' && (
          <div className="rounded-md border border-border bg-muted/40 p-3">
            <p className="font-semibold">Esta cuenta pide una passkey</p>
            <p className="mt-1 text-xs text-muted-foreground">
              WhatsApp necesita una passkey para terminar de vincular este número. Termina el
              vínculo desde el Manager de Evolution GO: ese paso no se hace desde aquí. Cuando
              termine, este panel lo detecta solo.
            </p>
          </div>
        )}

        {qr.data && qr.data.kind !== 'passkey' && (
          <div className="flex flex-col items-center gap-2">
            <img
              src={qrImageSrc(qr.data.qrImageBase64)}
              alt="QR para conectar WhatsApp"
              className="h-64 w-64 rounded border border-border bg-white p-2"
            />
            <ol className="list-decimal pl-5 text-xs text-muted-foreground">
              <li>Abre WhatsApp en tu teléfono.</li>
              <li>Configuración → Dispositivos vinculados → Vincular dispositivo.</li>
              <li>Escanea el código de arriba.</li>
            </ol>
          </div>
        )}
      </div>
    </Modal>
  );
}
